import { useEffect, useState } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import useTranslation from 'next-translate/useTranslation'
import * as gtag from '@lib/utilities/analytics'
import Turnstile from '@components/utils/turnstile'
import PhoneLink from '@components/utils/phone-link'
import { IconPhone } from '@components/utils/icons'

/**
 * The callback-request form, per the artifact's modal-card design:
 * kicker → heading → note → two stacked labelled fields → Turnstile →
 * full-width «Καλέστε με» with the phone icon → «ή τηλεφωνήστε:» alt line.
 * Two fields DELIBERATELY (name + phone): it is a callback request, not a
 * booking system — every extra field costs completions and the clinic
 * phones back anyway.
 *
 * `view: 'modal'` renders inside the popup (D47); `view: 'boxed'` is the
 * same stack on /kleiste-rantevou and the contact page — one form, two
 * frames, so the popup and its no-JS fallback can never drift apart.
 */
export interface AppointmentFormProps {
  appointmentHeading: string,
  appointmentPrompt: string,
  view?: 'boxed' | 'modal',
  /** funnel channel for analytics: `modal:<source>` (popup), 'page'
   *  (/kleiste-rantevou) or 'section' (embedded in a content page) — the
   *  report splits opens/submits per channel on it (P11-d). Falls back to
   *  the view name so an unwired caller still labels something. */
  placement?: string,
  closeBookAppointmentModal?: () => void
}

type FormValues = {
  name: string,
  phoneNumber: string
}

const FIELD_CLS =
  'w-full rounded-[10px] border-[1.5px] border-rule bg-white px-3.5 py-3 text-ink transition-colors ' +
  'placeholder:text-muted/60 hover:border-violet-400 focus:border-violet-700 focus:outline-none ' +
  'focus:ring-[3px] focus:ring-violet-050'

export default function AppointmentForm(props: AppointmentFormProps) {
  const { t } = useTranslation('main')
  const [outcome, setOutcome] = useState('')
  const [hasErrors, setHasErrors] = useState(false)
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>()

  const placement = props.placement ?? props.view ?? 'boxed'

  // the modal fires appointment_open at the click that opens it; the page
  // and embedded-section forms have no such click — their funnel entry is
  // BEING SEEN, so mount counts as the open (once per pageview by nature)
  useEffect(() => {
    if (placement === 'page' || placement === 'section') {
      gtag.event('appointment_open', { event_category: 'conversion', event_label: placement })
    }
  }, [placement])

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    fetch('api/appointment-requests', {
      method: 'POST',
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ...formData, turnstileToken })
    }).then((res) => {
      if (res.status === 200) {
        // one of the Ads conversion actions (item 13) — fires on the page the
        // visitor was on, so Ads attributes it to the landing page that earned it
        gtag.event('appointment_submit', { event_category: 'conversion', event_label: placement })
        setOutcome(t('booking.success'))
        setHasErrors(false)
        reset()
        props.closeBookAppointmentModal && setTimeout(props.closeBookAppointmentModal, 3000);
      } else {
        setOutcome(t('booking.failure'))
        setHasErrors(true)
      }
    })
  }

  return (
    // boxed: the card constrains ITSELF (max-w-xl) — the page's max-w-2xl
    // utility loses the max-width cascade against `.shell`, and a two-field
    // form should never sprawl to shell width anyway
    <div className={`flex flex-col gap-3 ${props.view === 'modal' ? '' : 'mx-auto w-full max-w-xl rounded-2xl border border-rule bg-white p-6 shadow-[0_26px_60px_-20px_rgba(23,17,31,0.18)] md:p-8'}`}>
      {/* no text-transform: uppercasing Greek strips the tonos (D50); the
          artifact's .modal-k relies on weight + letterspacing alone */}
      <span className="text-xs font-extrabold tracking-[0.09em] text-violet-700">
        {t('booking.kicker')}
      </span>
      <h3 className="text-xl font-extrabold text-ink">{props.appointmentHeading}</h3>
      <p className="text-[0.9375rem] text-muted">{props.appointmentPrompt}</p>

      <form className="flex flex-col gap-3" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="name" className="text-[0.95rem] font-bold text-ink">
            {t('booking.nameLabel')}
          </label>
          <input {...register('name', { required: true })}
            id="name" name="name" type="text" autoComplete="name"
            placeholder={t('booking.namePlaceholder')}
            aria-invalid={errors.name ? 'true' : undefined}
            className={`${FIELD_CLS} ${errors.name ? 'border-2 border-error' : ''}`} />
          {errors.name ? <span className="text-xs font-bold text-error">{t('booking.required')}</span> : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="phoneNumber" className="text-[0.95rem] font-bold text-ink">
            {t('booking.phoneLabel')}
          </label>
          <input {...register('phoneNumber', { required: true })}
            id="phoneNumber" name="phoneNumber" type="tel" autoComplete="tel"
            placeholder={t('booking.phonePlaceholder')}
            aria-invalid={errors.phoneNumber ? 'true' : undefined}
            className={`${FIELD_CLS} ${errors.phoneNumber ? 'border-2 border-error' : ''}`} />
          {errors.phoneNumber ? <span className="text-xs font-bold text-error">{t('booking.required')}</span> : null}
        </div>

        {/* Turnstile runs interaction-only: invisible for almost everyone,
            it materialises with its own UI only when Cloudflare demands a
            check — so NO reserved chip row (the artifact's tinted chip was a
            mock representation; an empty strip read as a broken element). */}
        <Turnstile onToken={setTurnstileToken} />

        <button type="submit"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-violet-700 px-6 py-3.5 font-extrabold text-white transition-colors hover:bg-violet-800">
          <IconPhone width={17} height={17} aria-hidden />
          {t('booking.submit')}
        </button>
      </form>

      <span className="flex items-center justify-center gap-1.5 text-center text-sm text-muted">
        {t('booking.alt')}
        <PhoneLink variant="plain" placement={props.view === 'modal' ? 'modal-alt' : 'form-alt'} />
      </span>

      {outcome ? (
        <span role="status" className={`block text-center text-sm font-bold ${hasErrors ? 'text-error' : 'text-success'}`}>
          {outcome}
        </span>
      ) : null}
    </div>
  )
}
